// Унифицированный 401 → refresh → retry перехватчик ответов axios.
//
// Подключается из http/index.js и http/apiJSON.js. Если refresh не удался —
// чистим auth-данные и редиректим на /login (если ещё не там).
//
// Используется один in-flight refresh-промис, чтобы параллельные 401 не
// дёргали /token/refresh/ многократно.

import axios from 'axios'

export const API_URL =
  process.env.REACT_APP_API_URL || 'https://sys.chu.kg/api/'

let refreshPromise = null

function clearAuthAndRedirect() {
  try {
    localStorage.removeItem('token')
    localStorage.removeItem('refresh')
    localStorage.removeItem('login_roles')
    localStorage.removeItem('login_primary_role')
    localStorage.removeItem('user')
  } catch (_) {
    /* ignore */
  }
  if (typeof window !== 'undefined') {
    const path = window.location.pathname || ''
    // Не редиректим, если уже на /login или /recover-*.
    if (
      !path.startsWith('/login') &&
      !path.startsWith('/recover-password') &&
      !path.startsWith('/recover-pin') &&
      !path.startsWith('/account-locked')
    ) {
      window.location.href = '/login'
    }
  }
}

async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise
  const refresh = (() => {
    try {
      return localStorage.getItem('refresh')
    } catch (_) {
      return null
    }
  })()
  if (!refresh) return Promise.reject(new Error('no_refresh_token'))

  refreshPromise = axios
    .post(
      `${API_URL.replace(/\/$/, '')}/account/token/refresh/`,
      { refresh },
      { headers: { 'Content-Type': 'application/json' } },
    )
    .then((res) => {
      const { access, refresh: newRefresh } = res.data || {}
      if (!access) throw new Error('no_access_in_refresh_response')
      try {
        localStorage.setItem('token', access)
        if (newRefresh) localStorage.setItem('refresh', newRefresh)
      } catch (_) {
        /* ignore */
      }
      return access
    })
    .finally(() => {
      // Освобождаем in-flight промис в любом случае.
      setTimeout(() => {
        refreshPromise = null
      }, 0)
    })

  return refreshPromise
}

/**
 * Подключает 401-перехватчик к axios-инстансу.
 * @param {import('axios').AxiosInstance} instance
 */
export function attachAuthRefresh(instance) {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status
      const original = error?.config || {}
      // Не пытаемся рефрешить запрос самого refresh'а или login'а.
      const url = (original.url || '').toString()
      const isAuthRoute =
        url.includes('/account/token/') || url.includes('/account/login/')

      if (status === 401 && !original._retry && !isAuthRoute) {
        original._retry = true
        try {
          const newToken = await refreshAccessToken()
          if (!original.headers) original.headers = {}
          original.headers.Authorization = `Bearer ${newToken}`
          return instance(original)
        } catch (_e) {
          clearAuthAndRedirect()
          return Promise.reject(error)
        }
      }

      // 423 / locked → отправляем на страницу блокировки (без чистки токена).
      if (status === 423 && typeof window !== 'undefined') {
        const path = window.location.pathname || ''
        if (!path.startsWith('/account-locked')) {
          window.location.href = '/account-locked/'
        }
      }

      return Promise.reject(error)
    },
  )
}

export default attachAuthRefresh
